// Hermes streams a turn as server-sent events, where the event name is on its own `event:` line
// and the payload on the `data:` line after it. EventSource cannot send an Authorization header,
// so the chat reads the body itself and this turns the bytes into events.

export function createSseParser(onEvent) {
  let buffer = "";
  let name = "";

  return function push(chunk) {
    buffer += chunk;
    let index = buffer.indexOf("\n");
    while (index >= 0) {
      const line = buffer.slice(0, index).replace(/\r$/, "");
      buffer = buffer.slice(index + 1);
      if (line.startsWith("event:")) {
        name = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        const raw = line.slice(5).trim();
        if (raw) {
          try {
            onEvent(name || "message", JSON.parse(raw));
          } catch {
            // A half-written or non-JSON line is skipped; the next event still arrives.
          }
        }
      } else if (line === "") {
        name = "";
      }
      index = buffer.indexOf("\n");
    }
  };
}

/** Reads a streaming response to the end, handing each event to `onEvent`. */
export async function readEventStream(response, onEvent) {
  const reader = response.body?.getReader?.();
  if (!reader) throw new Error("streaming_unsupported");
  const decoder = new TextDecoder();
  const push = createSseParser(onEvent);
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      push(decoder.decode(value, { stream: true }));
    }
    // A final event can end at EOF without a newline; also flush split UTF-8 bytes.
    push(`${decoder.decode()}\n\n`);
  } finally {
    reader.releaseLock();
  }
}

/**
 * What one turn's events mean for the panel: the answer as it is written, the tools the agent
 * reaches for, and how the turn ended. Two more fields serve the Stop button: `runId`, the first
 * `run_id` any event carried (null until one does), so a stop can name the run it means; and
 * `interrupted`, whether the agent reports the turn was cut short (read from `assistant.completed`
 * and `run.completed`, false otherwise). When `assistant.completed` is interrupted the text stays
 * as it was streamed and the event's `content` is ignored: the agent's final text on an interrupt
 * is its own "Operation interrupted: ..." sentence, and the person should see what the agent had
 * actually written. An uninterrupted `assistant.completed` still replaces the pieces with the
 * whole answer.
 */
export function reduceTurnEvent(state, name, payload) {
  const runId = state.runId ?? (typeof payload?.run_id === "string" ? payload.run_id : null);
  const carried = { ...state, runId, interrupted: state.interrupted ?? false };
  switch (name) {
    case "assistant.delta":
      return { ...carried, text: state.text + String(payload?.delta ?? "") };
    case "tool.started":
      return { ...carried, tool: String(payload?.tool_name || payload?.tool || "") || state.tool };
    case "tool.completed":
      return { ...carried, tool: "" };
    case "assistant.completed": {
      // Which model answered matters: the stand-in means the real one could not be reached.
      const model = String(payload?.runtime?.model ?? state.model ?? "");
      const interrupted = Boolean(payload?.interrupted);
      return !interrupted && typeof payload?.content === "string" && payload.content
        ? { ...carried, text: payload.content, tool: "", model, interrupted }
        : { ...carried, tool: "", model, interrupted };
    }
    case "run.completed":
      return { ...carried, status: "done", tool: "", model: String(payload?.runtime?.model ?? state.model ?? ""), interrupted: Boolean(payload?.interrupted) };
    case "run.failed":
    case "error":
      return { ...carried, status: "failed", tool: "" };
    default:
      return carried;
  }
}
